import { boardFromCells, reveal } from './puzzle';
import type { App } from './app';
import type { SaveStore } from './core/storage';
import type { TapResult } from './scenes/PopupScene';
import { cellCenter, worldToScreen } from './play/layout';
import { setToday, today } from './core/clock';

declare global {
  interface Window {
    render_game_to_text?: () => string;
    advanceTime?: (ms: number) => void;
    __PAPER_TOWN_TEST__?: {
      setCells: (cells: number[][]) => void;
      /**
       * 摆棋盘并指定玩法状态：cells 同 setCells；rot / down 按 tileId 下标（缺省 = 全 0 / 全正面）。
       * 与开局一样会先跑一遍背面块翻开规则。
       */
      setBoard: (cells: number[][], rot?: number[] | null, down?: boolean[] | null) => void;
      /** 完整提示：{ move } / { place } / { rotate }（不改变状态） */
      hint: () => unknown;
      cellScreen: (row: number, col: number) => { x: number; y: number };
      hintMove: () => { anchor: number; to: { row: number; col: number } } | null;
      /** 暂停主循环推进（仍会渲染），之后只由 advanceTime 推进，便于截取动画中间帧 */
      setPaused: (paused: boolean) => void;
      /** 注入一次相对正中的倾斜（度）：beta = 前后俯仰，gamma = 左右；走与 deviceorientation 相同的路径 */
      setTilt: (beta: number, gamma: number) => void;
      /** 猫在当前画面上的屏幕坐标（CSS px，画布左上原点） */
      catScreen: () => { x: number; y: number };
      /** 符文在当前画面上的屏幕坐标（没有符文 = null） */
      runeScreen: () => { x: number; y: number } | null;
      /** 立体书里的一次点按（等价于不移动的 pointerdown/up），返回判定结果 */
      tapPopup: (x: number, y: number) => TapResult | null;
      lastTap: () => TapResult | null;
      /** 纸层实际覆盖的屏幕范围（CSS px） */
      pictureBounds: () => { left: number; top: number; right: number; bottom: number };
      /** 最近一帧的 renderer.info.render */
      renderInfo: () => { calls: number; triangles: number; points: number; lines: number };
      /** renderer.info.memory：GPU 上现存的几何体 / 纹理数 */
      gpuMemory: () => { geometries: number; textures: number; programs: number };
      /** 注入本地时间（小时，0..24）；null = 恢复真实时间 */
      setHour: (h: number | null) => void;
      /** 热点在屏幕上的位置与半径（CSS px） */
      hotspotScreen: (id: string) => { x: number; y: number; r: number } | null;
      lastMapTap: () => { id: string | null; state: string | null } | null;
      /** 地图 teaser 标签的最新布局（CSS px）与各自压住热点的面积 */
      teaserLayout: () => { id: string; rect: { l: number; t: number; r: number; b: number }; overlap: number }[];
      /** 标记当前城市全部地点通关并触发终章 */
      completeAll: () => Promise<void>;
      /** 把某城前 count 个地点（按 unlockOrder）标成通关 */
      completePlaces: (themeId: string, count: number) => Promise<void>;
      /** 注入"今天"（本地日期 YYYY-MM-DD）；null = 恢复真实日期。会重画当前页 */
      setToday: (date: string | null) => void;
      today: () => string;
      /** Android 返回键（与 Capacitor backButton 监听同一个处理函数）；首页上返回 'exit' */
      hardwareBack: () => 'exit' | 'back';
      /** 存档原文（JSON） */
      saveJson: () => string;
      /** 重新读取安全区并重排（注入 --safe-top 后调用） */
      relayout: () => void;
    };
    __THREE_GAME_DIAGNOSTICS__?: { frame: number };
  }
}

export function installTestHooks(o: {
  app: App;
  store: SaveStore;
  setLoopPaused: (paused: boolean) => void;
  resize: () => void;
  hardwareBack: () => 'exit' | 'back';
}): void {
  const { app } = o;
  const session = () => {
    const s = app.session;
    if (!s) throw new Error('not on a puzzle page');
    return s;
  };
  const popup = () => {
    const p = app.page === 'viewPopup' ? app.view?.popup : app.play?.popup;
    if (!p) throw new Error('no pop-up book on this page');
    return p;
  };
  window.render_game_to_text = () => JSON.stringify(app.toText());
  window.advanceTime = (ms: number) => {
    for (let t = 0; t < ms; t += 16) app.update(Math.min(16, ms - t));
    app.render();
  };
  window.__PAPER_TOWN_TEST__ = {
    setCells: (cells) => session().setBoardForTest(boardFromCells(cells)),
    setBoard: (cells, rot, down) => {
      const b = boardFromCells(cells);
      if (rot) b.rot = [...rot];
      if (down) {
        b.down = [...down];
        b.down = reveal(b).down;
      }
      session().setBoardForTest(b);
    },
    hint: () => session().hintFull(),
    cellScreen: (row, col) => {
      const s = session();
      const c = cellCenter(app.play!.board.boardRect, s.grid, { row, col });
      return worldToScreen(s.viewport, c.x, c.y);
    },
    hintMove: () => session().hintMove(),
    setPaused: (paused) => o.setLoopPaused(paused),
    setTilt: (beta, gamma) => app.gyro.inject(beta, gamma),
    catScreen: () => popup().catScreen(),
    runeScreen: () => popup().runeScreen(),
    tapPopup: (x, y) => app.tap(x, y),
    lastTap: () => app.lastTap,
    pictureBounds: () => popup().pictureScreenBounds(),
    renderInfo: () => {
      const r = app.renderer.info.render;
      return { calls: r.calls, triangles: r.triangles, points: r.points, lines: r.lines };
    },
    gpuMemory: () => {
      const i = app.renderer.info;
      return { geometries: i.memory.geometries, textures: i.memory.textures, programs: i.programs?.length ?? 0 };
    },
    setHour: (h) => (app.hourOverride = h),
    hotspotScreen: (id) => app.hotspotScreen(id),
    lastMapTap: () => app.lastMapTap,
    teaserLayout: () => app.teaserLayout(),
    completeAll: () => app.completeAllForTest(),
    completePlaces: (themeId, count) => app.completePlacesForTest(themeId, count),
    setToday: (date) => {
      setToday(date);
      app.refreshDomPage();
    },
    today: () => today(),
    hardwareBack: () => o.hardwareBack(),
    saveJson: () => JSON.stringify(o.store.save),
    relayout: () => o.resize(),
  };
}
